import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { Lead } from '../../types/crm';
import SimpleDateRangePicker from '../SimpleDateRangePicker';
import ReactSelect from "react-select";
import { CalendarDays, Clock3, MapPin, UserRound } from "lucide-react";

interface FilterSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leads: Lead[];
  onFilter: (filteredLeads: Lead[]) => void;
  onFiltersChange?: (count: number) => void;
  rmList: { id: string | number; name: string }[];
  onApplyFilters: (filters: any) => void;
  cleardata?: boolean;
  leadLocation?: any[];
}

const FilterSidebar = ({
  leadLocation,
  open,
  onOpenChange,
  leads,
  onFilter,
  onFiltersChange,
  rmList,
  onApplyFilters,
  cleardata,
}: FilterSidebarProps) => {
  const [filters, setFilters] = useState({
    status: [] as string[],
    leadSource: '',
    leadRM: '',
    createdDateFrom: null as Date | null,
    createdDateTo: null as Date | null,
    lastCallDateFrom: null as Date | null,
    lastCallDateTo: null as Date | null,
    location: [] as string[],
  });

  const statusOptions = [
    { id: 1, value: 'New' },
    { id: 2, value: 'Contacted' },
    { id: 3, value: 'Interested' },
    { id: 4, value: 'Meeting Setup' },
    { id: 5, value: 'Proposal Sent' },
    { id: 6, value: 'Won' },
    { id: 7, value: 'Lost' },
    { id: 8, value: 'Not Interested' },
    { id: 11, value: 'Follow Up' },
  ];

  const locationOptions = leadLocation?.map((loc: string) => ({
    label: loc,
    value: loc,
  }));
  const leadRMs = rmList || [];

  const handleStatusChange = (status: string, checked: boolean) => {
    setFilters((prev) => ({
      ...prev,
      status: checked ? [...prev.status, status] : prev.status.filter((s) => s !== status),
    }));
  };

  const applyFilters = () => {
    onApplyFilters(filters);
    onOpenChange(false);
  };

  const clearFilters = () => {
    const resetFilters = {
      status: [],
      leadSource: '',
      leadRM: '',
      createdDateFrom: null,
      createdDateTo: null,
      lastCallDateFrom: null,
      lastCallDateTo: null,
      location: [],
    };
    setFilters(resetFilters);
    onApplyFilters(resetFilters);
    onOpenChange(false);
  };

  const clearfilterdata = () => {
    setFilters({
      status: [],
      leadSource: '',
      leadRM: '',
      createdDateFrom: null,
      createdDateTo: null,
      lastCallDateFrom: null,
      lastCallDateTo: null,
      location: [],
    });
  };

  useEffect(() => {
    const activeCount =
      filters.status.length +
      (filters.leadSource ? 1 : 0) +
      (filters.leadRM ? 1 : 0) +
      (filters.createdDateFrom ? 1 : 0) +
      (filters.createdDateTo ? 1 : 0) +
      (filters.lastCallDateFrom ? 1 : 0) +
      (filters.lastCallDateTo ? 1 : 0);

    onFiltersChange?.(activeCount);
  }, [filters, onFiltersChange]);

  useEffect(() => {
    if (cleardata) clearfilterdata();
  }, [cleardata]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[352px] max-w-[92vw] bg-white border-l border-slate-200 flex flex-col max-h-screen p-0 shadow-[-14px_0_32px_rgba(15,23,42,0.08)]">
        <SheetHeader className="flex-shrink-0 border-b border-slate-200 px-5 py-3.5">
          <SheetTitle className="text-[18px] font-semibold tracking-tight text-slate-900">
            Filter Leads
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 pt-1.5 pb-2.5">
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="mb-0 text-[14px] leading-none font-semibold text-slate-900">Status</Label>
              <div className="grid grid-cols-2 gap-x-5 gap-y-2 pt-2">
                {statusOptions.map((status) => (
                  <label key={status.id} className="flex items-center gap-2 text-[13px] text-slate-600">
                    <Checkbox
                      id={status.value}
                      checked={filters.status.includes(status.value)}
                      onCheckedChange={(checked) => handleStatusChange(status.value, checked as boolean)}
                      className="h-4.5 w-4.5 rounded-full border-slate-300 data-[state=checked]:border-[#3f8b98] data-[state=checked]:bg-[#3f8b98]"
                    />
                    <span className="leading-none">{status.value}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="space-y-3 border-t border-slate-200 pt-3">
              <div className="space-y-2">
                <Label className="text-[14px] font-semibold text-slate-900">Lead RM</Label>
                <div className="relative">
                  <UserRound className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Select
                    value={filters.leadRM}
                    onValueChange={(value) =>
                      setFilters((prev) => ({ ...prev, leadRM: value === 'all' ? '' : value }))
                    }
                  >
                    <SelectTrigger className="h-11 rounded-xl border-slate-200 bg-white px-4 pl-10 text-[13px] text-slate-700 shadow-sm focus:border-[#3f8b98] focus:ring-[#3f8b98]/15">
                      <SelectValue placeholder="Select RM" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="all">All RMs</SelectItem>
                      {leadRMs.map((rm) => (
                        <SelectItem key={rm.id} value={String(rm.id)}>
                          {rm.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[14px] font-semibold text-slate-900">Location</Label>
                <div className="relative">
                  <MapPin className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <ReactSelect
                    isMulti
                    options={locationOptions}
                    value={filters.location.map((loc: string) => ({
                      label: loc,
                      value: loc,
                    }))}
                    onChange={(selected: any) =>
                      setFilters((prev: any) => ({
                        ...prev,
                        location: selected ? selected.map((s: any) => s.value) : [],
                      }))
                    }
                    placeholder="All Locations"
                    className="text-sm"
                    classNames={{
                      control: () =>
                        "min-h-[44px] rounded-xl border border-slate-200 bg-white pl-9 shadow-sm focus:border-[#3f8b98] focus:ring-[#3f8b98]/15",
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[14px] font-semibold text-slate-900">Created Date Range</Label>
                <SimpleDateRangePicker
                  dateRange={{
                    from: filters.createdDateFrom ?? undefined,
                    to: filters.createdDateTo ?? undefined,
                  }}
                  onDateRangeChange={(range) => {
                    setFilters((prev) => ({
                      ...prev,
                      createdDateFrom: range?.from ?? null,
                      createdDateTo: range?.to ?? null,
                    }));
                  }}
                  buttonClassName="h-11 rounded-xl border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[14px] font-semibold text-slate-900">Last Call Date Range</Label>
                <SimpleDateRangePicker
                  dateRange={{
                    from: filters.lastCallDateFrom ?? undefined,
                    to: filters.lastCallDateTo ?? undefined,
                  }}
                  onDateRangeChange={(range) => {
                    setFilters((prev) => ({
                      ...prev,
                      lastCallDateFrom: range?.from ?? null,
                      lastCallDateTo: range?.to ?? null,
                    }));
                  }}
                  buttonClassName="h-11 rounded-xl border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 bg-white px-5 py-4">
          <div className="flex gap-3">
            <Button
              onClick={applyFilters}
              className="flex-1 h-11 rounded-2xl bg-[#3f8b98] text-[13px] font-medium text-white shadow-[0_10px_20px_rgba(63,139,152,0.22)] hover:bg-[#357983]"
            >
              Apply Filters
            </Button>
            <Button
              onClick={clearFilters}
              variant="outline"
              className="flex-1 h-11 rounded-2xl border-slate-200 bg-white text-[13px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
            >
              Clear All
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FilterSidebar;
